"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.doesRayIntersectArc = doesRayIntersectArc;
exports.doesRayIntersectLineSegment = doesRayIntersectLineSegment;
exports.isPointInsidePart = isPointInsidePart;
exports.isPointInsidePoly = isPointInsidePoly;
exports.isPointInsidePolys = isPointInsidePolys;
var _vector = require("./vector");
var _checkAngles = require("./checkAngles");
var _intersectEntities = require("./intersectEntities");
/**
 * Tests whether a point is inside a part
 * @param {Point} point The point to test as an object: {x, y}
 * @param {*} part The part
 * @param {Boolean} useNonTopologicalContours If true, will consider non topological contours instead of ignoring them. Default = false
 * @param {Boolean} outerContoursOnly If true, will only consider outermost contours. Points inside holes will still be considered to be inside the part.
 */
function isPointInsidePart(point, part, useNonTopologicalContours = false, outerMostContoursOnly = false) {
  // console.log(`Testing if point ${JSON.stringify(point)} is inside part ${part.id}`)

  if ((0, _vector.isInBbox)(point, part.arcified.bbox)) {
    // console.log(`Inside bbox of partCoords ${i}`)

    // Now, cast a ray from p to positive infinity in the x-direction, and count how many entities it intersects.
    // (Some circles and arcs need not be tested if we are sure there are exactly 0 or 2 intersections.)

    let votesInside = 0;
    let votesOutside = 0;

    // Continue until there is a majority voting one way or another
    while (Math.abs(votesInside - votesOutside) < 2) {
      // Create a "ray" in a randomish direction (does not need to be uniform in the circle)
      let end = {
        x: Math.random() - 0.5,
        y: Math.random() - 0.5
      };
      let bigEnoughRadius = part.arcified.bbox.max.x - part.arcified.bbox.min.x + part.arcified.bbox.max.y - part.arcified.bbox.min.y;
      end = (0, _vector.add)((0, _vector.mul)((0, _vector.normalize)(end), bigEnoughRadius), point);
      let ray = {
        type: 'LINE',
        start: {
          ...point
        },
        end,
        reversed: false
      };

      // Verify ray end is _not_ in the part bbox
      if ((0, _vector.isInBbox)(ray.end, part.arcified.bbox)) {
        throw new Error('Ray does not extend beyond edge of bbox!');
      }

      // Count intersections between the ray and each contour.
      let nIntersections = 0;
      for (let i = 0; i < part.arcified.contours.length; i++) {
        let c = part.arcified.contours[i];
        if (!c.isClosed) continue;
        if (!useNonTopologicalContours && c.type === 'nonTopological') continue;
        if (outerMostContoursOnly && !c.isOuterMost) continue;

        // If the ray does not start inside a contour's bbox, then skip that contour since it can only intersect an even number of times.
        if ((0, _vector.isInBbox)(point, c.bbox)) {
          for (let j = 0; j < c.entities.length; j++) {
            let e = c.entities[j];
            nIntersections += (0, _intersectEntities.intersectEntities)(ray, e).length;
          }
        }
      }
      if (nIntersections % 2 === 1) {
        votesInside++;
      } else {
        votesOutside++;
      }
    }
    return votesInside > votesOutside;
  }

  // Point is not in the part's bbox
  return false;
}

/**
 * Determine whether the given point is inside a closed polyline.
 * @param {Point} point The point to test
 * @param {Array} poly Array of points that form a closed polyline.
 * @param {boolean} [isLastPointRepeated] Whether the final point of this closed polyline is repeated. Default: true
 * @param {Bbox} [polyBbox] The bounding box of the polyline. Optional.
 */
function isPointInsidePoly(point, poly, isLastPointRepeated = true, polyBbox) {
  if (polyBbox && !(0, _vector.isInBbox)(point, polyBbox)) {
    return false;
  }

  // Now, cast a ray from p to positive infinity in the x-direction, and count how many line segments it intersects.
  const endIdx = isLastPointRepeated ? poly.length - 1 : poly.length;
  let nIntersections = 0;
  for (let j = 0; j < endIdx; j++) {
    let p0 = poly[j];
    let p1 = poly[(j + 1) % poly.length];
    if (doesRayIntersectLineSegment(point, p0, p1)) {
      nIntersections++;
    }
  }
  if (nIntersections % 2 === 1) {
    return true;
  }
  return false;
}

/**
 * Determine whether the given point is inside a group of closed, non-intersecting polylines. Polylines inside other polylines are taken to be "holes", and points in holes are not inside the group of polylines.
 * @param {Point} point The point to test
 * @param {Array} polys Array of polys, each containing an array of points
 * @param {boolean} areLastPointsRepeated Whether the final point of each polyline is repeated. Default: true 
 */
function isPointInsidePolys(point, polys, areLastPointsRepeated = true) {
  // Cast a ray from p to positive infinity in the x-direction, and count how many line segments in all polys it intersects.

  let nIntersections = 0;
  for (let i = 0; i < polys.length; i++) {
    let poly = polys[i];
    const endIdx = areLastPointsRepeated ? poly.length - 1 : poly.length;
    for (let j = 0; j < endIdx; j++) {
      let p0 = poly[j];
      let p1 = poly[(j + 1) % poly.length];
      if (doesRayIntersectLineSegment(point, p0, p1)) {
        nIntersections++;
      }
    }
  }
  if (nIntersections % 2 === 1) {
    return true;
  }
  return false;
}

/**
 * Determines whether a point lies to the left of a line segment, or in other words, if a ray beginning at point and
 * extending in the positive x-direction intersects the line segment given by start and end
 * @param point The point to test
 * @param start The first point of the line segment
 * @param end The second point of the line segment
 * @returns Whether there is exactly one intersection
 */
function doesRayIntersectLineSegment(point, start, end) {
  if (start.x >= point.x || end.x >= point.x) {
    // The line might intersect (part of the line is in the positive x-direction)
    if (start.y <= point.y && end.y > point.y || start.y > point.y && end.y <= point.y) {
      // Still might intersect (the line crosses the ray somewhere)
      // Find the point of intersection
      // t is how far along the line segment the point of intersection is
      let t = (point.y - start.y) / (end.y - start.y);
      let isectX = start.x + t * (end.x - start.x);
      if (isectX > point.x) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Tests whether a ray beginning at point and extending in the positive x-direction intersects the given arc exactly once
 * @param {Point} point The point to test
 * @param {Arc} e An entity with properties x, y, r, startAngle, and endAngle
 * @returns {boolean} Whether there is exactly one intersection
 */
function doesRayIntersectArc(point, e) {
  if (Math.abs(e.y - point.y) < e.r) {
    // The arc might touch the ray

    if ((0, _vector.norm2)((0, _vector.sub)(e, point)) < e.r * e.r) {
      // We are "inside" the arc
      // There must be exactly one or zero intersections.

      // The angle of intersection is wherever e.y + e.r * sin(angle) === p.y
      // asin always returns between -pi/2 and pi/2, which would be toward the right side of the arc, which is what we want

      let angleOfIsect = Math.asin((point.y - e.y) / e.r);
      // Now we can use startAngle and endAngle to know if the arc passes through angleOfIntersection 

      let startAngle = e.startAngle;
      let endAngle = e.endAngle;
      (0, _checkAngles.checkAngles)(e);

      // Ensure startAngle < angleOfIsect < startAngle + 2 PI
      while (angleOfIsect < startAngle) angleOfIsect += Math.PI * 2; // angleOfIsect too small
      while (angleOfIsect > startAngle + Math.PI * 2) angleOfIsect -= Math.PI * 2; // angleOfIsect too big

      if (angleOfIsect <= endAngle) {
        return true;
      }
    } else {
      // We're "outside" the arc, so we can use the starting and ending points to know
      if (e.x > point.x) {
        // The arc is to the right of us
        let pStart = {
          x: null,
          y: e.y + e.r * Math.sin(e.startAngle)
        };
        let pEnd = {
          x: null,
          y: e.y + e.r * Math.sin(e.endAngle)
        };
        if (pStart.y <= point.y && pEnd.y > point.y || pStart.y > point.y && pEnd.y <= point.y) {
          // One endpoint of the arc is above, one endpoint is below.
          return true;
        }
        // It's possible that both endpoints are above or below and that the arc still intersects the ray, but if it does, it would intersect twice.
      }
    }
  }

  return false;
}